import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileText, Search, Eye, Download } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OfferLettersPage = () => {
  const [offerLetters, setOfferLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    candidate_name: "",
    email: "",
    phone: "",
    address: "",
    designation: "",
    department: "",
    salary: "",
    joining_date: "",
    probation_period: 6,
    reporting_manager: "",
    work_schedule: "Monday to Saturday, 9:00 AM - 6:00 PM",
    benefits: "",
    responsibilities: "",
    acceptance_due_date: "",
    status: "draft"
  });

  const designations = ["Driver", "Manager", "Accountant", "Supervisor", "Coordinator", "HR Executive", "Admin"];
  const departments = ["Operations", "Finance", "HR", "Administration", "Logistics", "Fleet Management"];

  useEffect(() => {
    fetchOfferLetters();
  }, []);

  const fetchOfferLetters = async () => {
    try {
      const response = await axios.get(`${API_URL}/offer-letters`);
      setOfferLetters(response.data);
    } catch (error) {
      toast.error("Failed to fetch offer letters");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        salary: parseFloat(formData.salary),
        probation_period: parseInt(formData.probation_period),
        benefits: formData.benefits ? formData.benefits.split('\n').filter(b => b.trim()) : null,
        responsibilities: formData.responsibilities ? formData.responsibilities.split('\n').filter(r => r.trim()) : null
      };

      if (selectedLetter) {
        await axios.put(`${API_URL}/offer-letters/${selectedLetter.id}`, submitData);
        toast.success("Offer letter updated successfully");
      } else {
        await axios.post(`${API_URL}/offer-letters`, submitData);
        toast.success("Offer letter created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchOfferLetters();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleEdit = (letter) => {
    setSelectedLetter(letter);
    setFormData({
      candidate_name: letter.candidate_name,
      email: letter.email,
      phone: letter.phone,
      address: letter.address || "",
      designation: letter.designation,
      department: letter.department,
      salary: letter.salary.toString(),
      joining_date: letter.joining_date,
      probation_period: letter.probation_period,
      reporting_manager: letter.reporting_manager || "",
      work_schedule: letter.work_schedule || "Monday to Saturday, 9:00 AM - 6:00 PM",
      benefits: letter.benefits ? letter.benefits.join('\n') : "",
      responsibilities: letter.responsibilities ? letter.responsibilities.join('\n') : "",
      acceptance_due_date: letter.acceptance_due_date || "",
      status: letter.status
    });
    setDialogOpen(true);
  };

  const handlePreview = async (letter) => {
    try {
      const response = await axios.get(`${API_URL}/offer-letters/${letter.id}/html`);
      setPreviewHtml(response.data);
      setSelectedLetter(letter);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load preview");
    }
  };

  const handleDownload = (letter) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(previewHtml);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/offer-letters/${selectedLetter.id}`);
      toast.success("Offer letter deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedLetter(null);
      fetchOfferLetters();
    } catch (error) {
      toast.error("Failed to delete offer letter");
    }
  };

  const resetForm = () => {
    setSelectedLetter(null);
    setFormData({
      candidate_name: "",
      email: "",
      phone: "",
      address: "",
      designation: "",
      department: "",
      salary: "",
      joining_date: "",
      probation_period: 6,
      reporting_manager: "",
      work_schedule: "Monday to Saturday, 9:00 AM - 6:00 PM",
      benefits: "",
      responsibilities: "",
      acceptance_due_date: "",
      status: "draft"
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "border-gray-500/50 text-gray-400 bg-gray-500/10",
      sent: "border-blue-500/50 text-blue-400 bg-blue-500/10",
      accepted: "border-green-500/50 text-green-400 bg-green-500/10",
      rejected: "border-red-500/50 text-red-400 bg-red-500/10"
    };
    return styles[status] || styles.draft;
  };

  const filteredLetters = offerLetters.filter(letter =>
    letter.candidate_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    letter.letter_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    letter.designation.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse font-heading text-xl">Loading offer letters...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="offer-letters-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
            <FileText className="w-8 h-8 text-accent" />
            Offer Letters
          </h1>
          <p className="text-muted-foreground mt-1">Generate and manage offer letters for candidates</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="glow-primary bg-primary hover:bg-primary/90"
          data-testid="create-offer-letter-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Offer Letter
        </Button>
      </div>

      {/* Search */}
      <Card className="border-border bg-card">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by candidate name, letter number, or designation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-secondary/50 border-border"
              data-testid="offer-letter-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-border bg-card">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading">All Offer Letters ({filteredLetters.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Letter No.</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Candidate</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Designation</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Department</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Salary</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Joining Date</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Status</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLetters.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No offer letters found. Create your first offer letter to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLetters.map((letter) => (
                    <TableRow key={letter.id} className="border-border table-row-hover" data-testid={`offer-letter-row-${letter.id}`}>
                      <TableCell className="font-mono text-primary">{letter.letter_number}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-foreground">{letter.candidate_name}</p>
                          <p className="text-xs text-muted-foreground">{letter.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{letter.designation}</TableCell>
                      <TableCell className="text-muted-foreground">{letter.department}</TableCell>
                      <TableCell className="font-mono text-foreground">{formatCurrency(letter.salary)}</TableCell>
                      <TableCell className="text-muted-foreground">{letter.joining_date}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getStatusBadge(letter.status)}>
                          {letter.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handlePreview(letter)}
                            data-testid={`preview-offer-letter-${letter.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(letter)}
                            data-testid={`edit-offer-letter-${letter.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => { setSelectedLetter(letter); setDeleteDialogOpen(true); }}
                            data-testid={`delete-offer-letter-${letter.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {selectedLetter ? "Edit Offer Letter" : "Create Offer Letter"}
            </DialogTitle>
            <DialogDescription>
              {selectedLetter ? "Update offer letter details" : "Enter candidate details to generate an offer letter"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="candidate_name">Candidate Name</Label>
              <Input
                id="candidate_name"
                value={formData.candidate_name}
                onChange={(e) => setFormData({ ...formData, candidate_name: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="Rahul Sharma"
                required
                data-testid="offer-candidate-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="rahul@email.com"
                  required
                  data-testid="offer-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="+91 98765 43210"
                  required
                  data-testid="offer-phone-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="designation">Designation</Label>
                <Select value={formData.designation} onValueChange={(v) => setFormData({ ...formData, designation: v })}>
                  <SelectTrigger className="bg-secondary/50 border-border" data-testid="offer-designation-select">
                    <SelectValue placeholder="Select designation" />
                  </SelectTrigger>
                  <SelectContent>
                    {designations.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Select value={formData.department} onValueChange={(v) => setFormData({ ...formData, department: v })}>
                  <SelectTrigger className="bg-secondary/50 border-border" data-testid="offer-department-select">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="salary">Monthly Salary (₹)</Label>
                <Input
                  id="salary"
                  type="number"
                  value={formData.salary}
                  onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="35000"
                  required
                  data-testid="offer-salary-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="joining_date">Joining Date</Label>
                <Input
                  id="joining_date"
                  type="date"
                  value={formData.joining_date}
                  onChange={(e) => setFormData({ ...formData, joining_date: e.target.value })}
                  className="bg-secondary/50 border-border"
                  required
                  data-testid="offer-joining-date-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="probation_period">Probation Period (months)</Label>
                <Select value={formData.probation_period.toString()} onValueChange={(v) => setFormData({ ...formData, probation_period: parseInt(v) })}>
                  <SelectTrigger className="bg-secondary/50 border-border" data-testid="offer-probation-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 Months</SelectItem>
                    <SelectItem value="6">6 Months</SelectItem>
                    <SelectItem value="12">12 Months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {selectedLetter && (
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger className="bg-secondary/50 border-border" data-testid="offer-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="accepted">Accepted</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="address">Candidate Address (Optional)</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="123 Main Street, City, State"
                data-testid="offer-address-input"
              />
            </div>

            {/* Reporting Manager and Work Schedule */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="reporting_manager">Reporting Manager (Optional)</Label>
                <Input
                  id="reporting_manager"
                  value={formData.reporting_manager}
                  onChange={(e) => setFormData({ ...formData, reporting_manager: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="Department Head"
                  data-testid="offer-reporting-manager-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="acceptance_due_date">Acceptance Due Date (Optional)</Label>
                <Input
                  id="acceptance_due_date"
                  type="date"
                  value={formData.acceptance_due_date}
                  onChange={(e) => setFormData({ ...formData, acceptance_due_date: e.target.value })}
                  className="bg-secondary/50 border-border"
                  data-testid="offer-acceptance-date-input"
                />
              </div>
            </div>

            {/* Work Schedule */}
            <div className="space-y-2">
              <Label htmlFor="work_schedule">Work Schedule</Label>
              <Input
                id="work_schedule"
                value={formData.work_schedule}
                onChange={(e) => setFormData({ ...formData, work_schedule: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="Monday to Saturday, 9:00 AM - 6:00 PM"
                data-testid="offer-work-schedule-input"
              />
            </div>

            {/* Benefits */}
            <div className="space-y-2">
              <Label htmlFor="benefits">Benefits (One per line, Optional)</Label>
              <textarea
                id="benefits"
                value={formData.benefits}
                onChange={(e) => setFormData({ ...formData, benefits: e.target.value })}
                className="flex min-h-[80px] w-full rounded-md border border-border bg-secondary/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder="Health Insurance Coverage&#10;Provident Fund (PF)&#10;Annual Performance Bonus"
                data-testid="offer-benefits-input"
              />
              <p className="text-xs text-muted-foreground">Enter each benefit on a new line. Default benefits will be used if left empty.</p>
            </div>

            {/* Responsibilities */}
            <div className="space-y-2">
              <Label htmlFor="responsibilities">Key Responsibilities (One per line, Optional)</Label>
              <textarea
                id="responsibilities"
                value={formData.responsibilities}
                onChange={(e) => setFormData({ ...formData, responsibilities: e.target.value })}
                className="flex min-h-[80px] w-full rounded-md border border-border bg-secondary/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder="Perform duties as assigned&#10;Maintain punctuality&#10;Comply with company policies"
                data-testid="offer-responsibilities-input"
              />
              <p className="text-xs text-muted-foreground">Enter each responsibility on a new line. Default responsibilities will be used if left empty.</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="glow-primary bg-primary" data-testid="offer-submit-btn">
                {selectedLetter ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="bg-white text-black max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-black flex items-center justify-between">
              <span>Offer Letter Preview</span>
              <Button variant="outline" size="sm" onClick={handleDownload} data-testid="download-offer-letter">
                <Download className="w-4 h-4 mr-2" />
                Print
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div 
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Delete Offer Letter</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete offer letter "{selectedLetter?.letter_number}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-offer-letter">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OfferLettersPage;
